import React, { useEffect, useRef, useState } from 'react';
import { SbEditableContent } from 'storyblok-react';
import { AnyProps } from '../types';

interface AddMoreInputBlok extends SbEditableContent {
  input: SbEditableContent[];
  limit: string;
  button_label: string;
}

interface AddMoreInputProps extends AnyProps {
  blok: AddMoreInputBlok;
}

const styles = {
  wrapper: { display: 'flex', alignItems: 'flex-end', gap: '8px' },
  input: { width: '100%' },
  delete: {
    marginBottom: '18px',
    cursor: 'pointer',
    fill: 'var(--color-interactive--primary-idle)',
  },
  button: { marginTop: '18px' },
};

export const AddMoreInput = (props: AddMoreInputProps): JSX.Element => {
  const limit = Number.parseInt(props.blok.limit, 10) || 1;
  const original = props.blok.input[0];

  const [count, setCount] = useState(1);
  const inputs = useRef<HTMLDivElement[]>([]);
  const moreButton = useRef(null);

  const handleAddMore = () => {
    setCount((current) => Math.min(current + 1, limit));
  };

  const handleRemove = (index: number) => {
    for (let i = index; i < count - 1; i += 1) {
      const nextInput = inputs.current[i + 1].querySelector('roche-input');
      const currentInput = inputs.current[i].querySelector('roche-input');

      currentInput.setValue(nextInput.value);
      nextInput.setValue('');
    }

    setCount((current) => Math.max(current - 1, 1));
  };

  const resetMarginTop = (ref) => {
    const input = ref.querySelector('roche-input') as unknown as HTMLElement;
    input.setAttribute('style', 'margin-top: 0');
  };

  useEffect(() => {
    inputs.current.forEach(resetMarginTop);
    moreButton.current?.addEventListener('rocheButtonClick', handleAddMore);

    return () => {
      moreButton.current?.removeEventListener(
        'rocheButtonClick',
        handleAddMore,
      );
    };
  }, []);

  if (!original) return <></>;

  return (
    <div slot={props.slot}>
      <div>
        {Array.from({ length: count }).map((_, index) => {
          const Input = props.getComponent(original.component);

          const blok = {
            ...original,
            // eslint-disable-next-line no-underscore-dangle
            _uid: `${props.blok._uid}-${index + 1}`,
            name: original.name ? `${original.name}-${index + 1}` : undefined,
          };

          return (
            <div key={index} style={styles.wrapper}>
              <div
                style={styles.input}
                ref={(el) => {
                  inputs.current[index] = el;
                }}>
                <Input getComponent={props.getComponent} blok={blok} />
              </div>
              {count > 1 && (
                <roche-icon
                  icon="trash"
                  height="24px"
                  style={styles.delete}
                  tabIndex={0}
                  onClick={() => handleRemove(index)}
                />
              )}
            </div>
          );
        })}
      </div>
      <roche-button-global
        ref={moreButton}
        style={styles.button}
        appearance="tertiary"
        left-icon="plus"
        text={props.blok.button_label || 'Add more'}
        disabled={count === limit}
      />
    </div>
  );
};
